import React, { type SyntheticEvent, useState } from "react";
import { Image, Placeholder } from "semantic-ui-react";
import cn from "classnames";

import ImageAspectRatio from "../../enums/imageAspectRatio";

import placeholderImage from "../../images/placeholder-16_9.png";
import { Label } from "components/common/label/Label";

import "./croppedThumbnail.scss";
import { HiddenContent } from "components/HiddenContent/HiddenContent";
import { BaseLabel, type Color } from "components/common/BaseLabel/BaseLabel";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components/textTruncators/TextTruncators";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/pro-solid-svg-icons";

enum stretchMode {
  fullWidth = "full-width",
  fullHeight = "full-height",
}

interface Dimensions {
  width?: number;
  height?: number;
}

export interface Props {
  thumbnailUrl?: string | null;
  onClick?: () => void;
  placeholderContent?: React.ReactElement;
  className?: string;
  aspectRatio?: ImageAspectRatio;
  altText?: string;
  draggable?: boolean;
  children?: React.ReactNode;
  isHidden?: boolean;
  label?: string;
  labelColor?: Color;
  labelTooltipContent?: string;
  editable?: boolean;
}

const CroppedThumbnail: React.FC<Props> = ({
  thumbnailUrl,
  onClick,
  placeholderContent,
  className,
  altText,
  draggable,
  children,
  isHidden,
  label,
  labelColor,
  labelTooltipContent,
  aspectRatio = ImageAspectRatio.HD_16X9,
  editable,
}: React.PropsWithChildren<Props>) => {
  const [visible, setVisible] = useState(!!placeholderContent || !thumbnailUrl);
  const [dimensions, setDimensions] = useState<Dimensions>({});

  const onImgLoad = (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    setDimensions({ width: target.offsetWidth, height: target.offsetHeight });
    setVisible(true);
  };

  const renderContent = () => {
    if (placeholderContent && !thumbnailUrl) {
      return placeholderContent;
    }
    return (
      <>
        {editable && (
          <div className={"image-edit"}>
            <FontAwesomeIcon icon={faPenToSquare} size="xl" color="white" />
            <div className={"image-edit__background"} />
          </div>
        )}
        <Image
          className="thumbnail"
          src={thumbnailUrl || placeholderImage}
          onLoad={onImgLoad}
          onError={(e: SyntheticEvent<HTMLElement>) => ((e.target as HTMLImageElement).src = placeholderImage)}
          alt={altText}
          draggable={draggable}
        />
      </>
    );
  };

  const { width, height } = dimensions;
  let ratio: stretchMode = stretchMode.fullWidth;
  if (width && height) {
    ratio = height / width >= 9 / 16 ? stretchMode.fullWidth : stretchMode.fullHeight;
  }
  const visibility = visible ? "visible" : "";

  return (
    <div className={cn("cropped-thumbnail-root", className)}>
      <div className="thumbnail-container" style={{ aspectRatio }} onClick={onClick}>
        <div
          className={cn("thumbnail-wrapper", ratio, visibility, {
            "placeholder-content": placeholderContent && !thumbnailUrl,
          })}
          data-testid="thumbnail-wrapper"
        >
          {renderContent()}
          {label &&
            (labelTooltipContent ? (
              <BaseLabel
                color={labelColor}
                className={"thumbnail__label-with-tooltip"}
                basic={false}
                attached={"right"}
              >
                <Tooltip
                  target={<TextTruncate>{label}</TextTruncate>}
                  content={labelTooltipContent}
                  showAlways
                  position="top center"
                  offset={[0, 4]}
                />
              </BaseLabel>
            ) : (
              <Label className={"thumbnail__label"} text={label} color={labelColor} attached="right" basic={false} />
            ))}
        </div>
        {!visible && (
          <Placeholder fluid className="image-placeholder">
            <Placeholder.Image />
          </Placeholder>
        )}
        {children}
      </div>
      <HiddenContent variant="card" isHidden={!!isHidden} />
    </div>
  );
};

export default CroppedThumbnail;
